import {useTranslation} from 'react-i18next'
import type {ProjectListItem} from '../../../common/api/generated/model/projectListItem'
import Icon from './Icon'
import SceneGrid from './SceneGrid'
import UserAvatar from '../userAvatar/UserAvatar'
import {computeInitials} from '../userAvatar/initials'

interface TemplateCardProps {
  project: ProjectListItem
  onClick: (project: ProjectListItem) => void
  // Stable timestamp captured once by the parent at mount; keeps render pure.
  nowMs: number
}

const ONE_DAY_MS = 1000 * 60 * 60 * 24

const TemplateCard = ({project, onClick, nowMs}: TemplateCardProps) => {
  const {t} = useTranslation()
  const referenceDateStr = project.publishedAt ?? project.updatedAt
  const days = Math.max(0, Math.floor((nowMs - new Date(referenceDateStr).getTime()) / ONE_DAY_MS))
  const ageLabel = days === 0 ? t('templates.card.today') : t('templates.card.daysAgo', {count: days})
  const authorName = project.author?.name ?? null

  return (
    <button type="button" className="tpl-card" onClick={() => onClick(project)}>
      <div className="tpl-thumb-frame">
        {(project.isFree || project.isFeatured) && (
          <div className="tpl-badges">
            {project.isFree && (
              <div className="tpl-badge free">
                <Icon name="sparkle" size={11} /> {t('templates.card.free')}
              </div>
            )}
            {project.isFeatured && (
              <div
                className="tpl-badge featured"
                title={t('templates.card.featured')}
                aria-label={t('templates.card.featured')}
              >
                <Icon name="star" size={12} />
              </div>
            )}
          </div>
        )}
        <SceneGrid thumbnails={project.sceneThumbnails} alt={project.name} />
      </div>
      <div className="tpl-body">
        <div className="tpl-name">
          <span>{project.name}</span>
        </div>
        {project.tags.length > 0 && (
          <div className="tpl-tags">
            {project.tags.map((tag) => (
              <span key={tag} className="tpl-tag">
                {tag}
              </span>
            ))}
          </div>
        )}
        <div className="tpl-meta">
          {authorName && (
            <>
              <span className="tpl-author">
                <UserAvatar
                  seed={project.author?.id ?? null}
                  initials={computeInitials(authorName?.split(' ')[0], authorName?.split(' ').slice(1).join(' '))}
                  size={18}
                />
                {authorName}
              </span>
              <span className="meta-dot" />
            </>
          )}
          <span className="meta-item">
            <Icon name="fork" size={12} /> {project.usageCount.toLocaleString()}
          </span>
          <span className="meta-dot" />
          <span className="meta-item">{ageLabel}</span>
        </div>
      </div>
    </button>
  )
}

export default TemplateCard
